import { apiEventFollow, apiEventUnfollow } from "@/api/event";
import { toast } from "@/components/ui/showToast";
import { openLoginModal } from "@/contexts/login-modal-context";
import { isLogin } from "@/store/auth";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

/**
 * 关注/取消关注事件的逻辑 Hook
 * @param eventInfo 事件信息，包含 eventNo 和 followed 状态
 */
export function useEventFollow( eventInfo: { eventNo: string; followed: boolean } ) {
    const [ followed, setFollowed ] = useState( eventInfo.followed );
    const queryClient = useQueryClient();

    // 同步外部 props 的变化到内部状态
    useEffect( () => {
        setFollowed( eventInfo.followed );
    }, [ eventInfo.followed ] );

    const updateGlobalCache = ( eventNo: string, newFollowed: boolean ) => {
        // 1. 更新无限滚动列表缓存 (topicEvents)
        queryClient.setQueriesData( { queryKey: [ "topicEvents" ] }, ( oldData: any ) => {
            if ( !oldData ) return oldData;
            return {
                ...oldData,
                pages: oldData.pages.map( ( page: any ) => ( {
                    ...page,
                    records: page.records.map( ( item: any ) =>
                        item.eventNo === eventNo ? { ...item, followed: newFollowed } : item
                    ),
                } ) ),
            };
        } );

        // 2. 更新详情页缓存 (eventDetail)
        queryClient.setQueriesData( { queryKey: [ "eventDetail", "byEvent", eventNo ] }, ( oldData: any ) => {
            if ( !oldData ) return oldData;
            return { ...oldData, followed: newFollowed };
        } );

        // 3. 更新首页推荐位缓存 (cricketLiveEvents)
        queryClient.setQueriesData( { queryKey: [ "cricketLiveEvents" ] }, ( oldData: any ) => {
            if ( !Array.isArray( oldData ) ) return oldData;
            return oldData.map( ( item: any ) => item.eventNo === eventNo ? { ...item, followed: newFollowed } : item );
        } );
    };

    const handleLoveClick = () => {
        if ( !isLogin() ) {
            openLoginModal( "auth-guard" );
            return;
        }

        const eventNo = eventInfo.eventNo;
        if ( followed ) {
            apiEventUnfollow( eventNo ).then( () => {
                setFollowed( false );
                updateGlobalCache( eventNo, false );
                toast.success( "Unfollowed successfully" );
            } );
        } else {
            apiEventFollow( eventNo ).then( () => {
                setFollowed( true );
                updateGlobalCache( eventNo, true );
                toast.success( "Followed successfully" );
            } );
        }
    };

    return { followed, handleLoveClick };
}
